import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowRight, Building2 } from "lucide-react";
import { SITE, AREAS_HOME } from "@/data/site";
import SEO from "@/components/SEO";

const OFFICES = {
    lisboa: {
        slug: "lisboa",
        city: "Lisboa",
        region: "Portugal Continental",
        heroTitle: "Advogado em Lisboa",
        heroSubtitle:
            "Assessoria jurídica de excelência no coração de Portugal Continental",
        intro: [
            "Desde 1995 que a ACD Advogados representa clientes particulares e empresas em todo o território de Portugal Continental. O nosso escritório de Lisboa, situado no Parque das Nações, é o ponto de partida para uma abordagem jurídica próxima, rigorosa e centrada em resultados.",
            "Com uma equipa multidisciplinar e experiência em Direito Penal, Civil, Trabalho, Comercial, Fiscal, Administrativo e Imigração, garantimos uma resposta ágil às necessidades de quem procura um advogado em Lisboa — seja para uma consulta pontual, uma questão empresarial complexa, um processo judicial ou um plano de avença mensal.",
        ],
        address: "Pátio das Fragatas, Av. Dom João II Nº 3 B, Loja 22, 1990-607 Lisboa",
        addressShort: "Parque das Nações, Lisboa",
        phones: ["21 716 76 64", "21 711 29 28"],
        hours: "Segunda a Sexta · 09h30 — 19h00",
        image: "https://customer-assets.emergentagent.com/job_advogados-staging/artifacts/nd0v3cg2_pexels-su-lopes-54155462-7789476.webp",
        leads: [
            {
                name: "Dra. Maria José Domingues",
                role: "Responsável Adjunta do escritório de Lisboa",
                photo: "/images/wp/2026/05/Dra.-Maria-Jose-Domingues-Responsavel-Adjunta-escritorio-ACD-Advogados-Lisboa.jpg",
            },
            {
                name: "Dra. Ana Cristina Domingues",
                role: "Fundadora da ACD Advogados",
                photo: "/images/wp/2026/04/Ana-Cristina-Advocacia-Portugal.jpg",
            },
        ],
        localHighlights: [
            "Atendimento presencial no Parque das Nações com fácil acesso por metro (Oriente) e comboio",
            "Estacionamento gratuito na envolvente do escritório",
            "Reuniões por videoconferência para clientes fora de Lisboa",
            "Cobertura de todos os tribunais da Comarca de Lisboa e Grande Lisboa",
        ],
        gmapsQuery: "ACD+Advogados+Lisboa+Parque+das+Nacoes",
    },
    "ponta-delgada": {
        slug: "ponta-delgada",
        city: "Ponta Delgada",
        region: "Açores",
        heroTitle: "Advogado em Ponta Delgada",
        heroSubtitle:
            "O único escritório da ACD Advogados nos Açores, ao serviço de todo o arquipélago",
        intro: [
            "O escritório da ACD Advogados em Ponta Delgada assegura assessoria jurídica em todas as ilhas dos Açores, com uma equipa que conhece profundamente a realidade regional, as especificidades da legislação insular e as dinâmicas dos tribunais da Comarca dos Açores.",
            "Se procura um advogado em Ponta Delgada, oferecemos apoio em Direito Civil, Comercial, Laboral, Imobiliário, Fiscal e Imigração, com atenção especial a matérias de arrendamento, sucessões, direito da família, contratação pública, contencioso administrativo e apoio a comunidades imigrantes.",
        ],
        address: "Ed. Rotunda, Av. Natália Correia 2, 9500-341 Ponta Delgada",
        addressShort: "Av. Natália Correia, Ponta Delgada",
        phones: ["296 102 222"],
        hours: "Segunda a Sexta · 09h30 — 19h00",
        image: "https://customer-assets.emergentagent.com/job_advogados-staging/artifacts/lb5qx4qn_pexels-francesco-ungaro-33515014.webp",
        leads: [
            {
                name: "Dra. Ana Cristina Domingues",
                role: "Fundadora da ACD Advogados",
                photo: "/images/wp/2026/04/Ana-Cristina-Advocacia-Portugal.jpg",
            },
            {
                name: "Dr. Bartolomeu Domingues",
                role: "Responsável do Escritório da ACD Advogados nos Açores",
                photo: "/images/wp/2026/05/Dr.-Bartolomeu-Domingues-Responsavel-Escritorio-ACD-Advogados-nos-Acores.-2.jpg",
            },
        ],
        localHighlights: [
            "Cobertura de todas as ilhas dos Açores — atendimento presencial em Ponta Delgada e deslocações agendadas às restantes ilhas",
            "Especialização em matérias de arrendamento e direito imobiliário açoriano",
            "Acompanhamento de processos AIMA e regularização de cidadãos estrangeiros nos Açores",
            "Reuniões por videoconferência para clientes noutras ilhas ou no continente",
        ],
        gmapsQuery: "ACD+Advogados+Ponta+Delgada+Natalia+Correia",
    },
};

const OfficePage = ({ slug }) => {
    const o = OFFICES[slug];
    if (!o) return null;

    return (
        <div data-testid={`page-office-${slug}`}>
            <SEO
                title={o.heroTitle}
                description={`${o.heroTitle} — ACD Advogados. ${o.heroSubtitle}. Contacte-nos: ${o.phones[0]}.`}
                canonicalPath={`/advogado-${slug}`}
                image={o.image}
            />

            {/* Hero */}
            <section className="relative py-20 md:py-28 px-6 bg-[var(--acd-navy-900)] text-white overflow-hidden">
                <div
                    className="absolute inset-0 opacity-25 bg-cover bg-center"
                    style={{ backgroundImage: `url(${o.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[var(--acd-navy-950)]/95 to-[var(--acd-navy-900)]/60" />
                <div className="relative z-10 max-w-6xl mx-auto">
                    <span className="acd-eyebrow">{o.region}</span>
                    <h1 className="text-4xl md:text-6xl font-bold leading-tight mt-3 mb-4 text-white">
                        {o.heroTitle}
                    </h1>
                    <p className="text-lg md:text-xl text-white/85 max-w-3xl">
                        {o.heroSubtitle}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <a
                            href={SITE.whatsappUrlAppointment}
                            target="_blank"
                            rel="noreferrer"
                            className="acd-btn-gradient"
                            data-testid={`office-cta-whatsapp-${slug}`}
                        >
                            Agendar consulta
                        </a>
                        <a
                            href={`tel:+351${o.phones[0].replace(/\s/g, "")}`}
                            className="acd-btn-outline !text-white !border-white/50 hover:!bg-white/10"
                        >
                            <Phone size={14} /> Ligar
                        </a>
                    </div>
                </div>
            </section>

            {/* Intro + contact card */}
            <section className="py-16 md:py-20 px-6">
                <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_360px] gap-12">
                    <div>
                        <span className="acd-eyebrow">Sobre o escritório</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-[var(--acd-navy-900)] mb-6 mt-3">
                            Um advogado próximo, em quem pode confiar
                        </h2>
                        {o.intro.map((p) => (
                            <p
                                key={p.substring(0, 30)}
                                className="text-[color:var(--acd-muted)] leading-relaxed mb-4"
                            >
                                {p}
                            </p>
                        ))}
                        <ul className="mt-6 space-y-3">
                            {o.localHighlights.map((h) => (
                                <li
                                    key={h.substring(0, 30)}
                                    className="flex gap-3 items-start text-[color:var(--acd-muted)]"
                                >
                                    <ArrowRight
                                        size={16}
                                        className="mt-1 shrink-0 text-[var(--acd-gold)]"
                                    />
                                    <span>{h}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <aside className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 h-fit sticky top-24">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--acd-blue-600)] to-[var(--acd-orange)] text-white flex items-center justify-center mb-4">
                            <Building2 size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-[var(--acd-navy-900)] mb-4">
                            Escritório em {o.city}
                        </h3>
                        <div className="space-y-3 text-sm text-[color:var(--acd-muted)]">
                            <div className="flex gap-3 items-start">
                                <MapPin size={16} className="mt-0.5 shrink-0" />
                                <span>{o.address}</span>
                            </div>
                            {o.phones.map((p) => (
                                <a
                                    key={p}
                                    href={`tel:+351${p.replace(/\s/g, "")}`}
                                    className="flex gap-3 items-center hover:text-[var(--acd-navy-900)]"
                                >
                                    <Phone size={16} className="shrink-0" />
                                    <span>{p}</span>
                                </a>
                            ))}
                            <a
                                href={`mailto:${SITE.email}`}
                                className="flex gap-3 items-center hover:text-[var(--acd-navy-900)]"
                            >
                                <Mail size={16} className="shrink-0" />
                                <span>{SITE.email}</span>
                            </a>
                            <div className="flex gap-3 items-start">
                                <Clock size={16} className="mt-0.5 shrink-0" />
                                <span>{o.hours}</span>
                            </div>
                        </div>
                        <a
                            href={`https://www.google.com/maps/search/?api=1&query=${o.gmapsQuery}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-5 acd-btn-outline w-full justify-center text-xs"
                        >
                            Ver no Google Maps
                        </a>
                    </aside>
                </div>
            </section>

            {/* Lead lawyers */}
            <section className="py-16 px-6 bg-[var(--acd-bg-soft)]">
                <div className="max-w-5xl mx-auto text-center">
                    <span className="acd-eyebrow">Responsáveis do escritório</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-[var(--acd-navy-900)] mt-3 mb-12">
                        Quem o vai receber
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-10 max-w-3xl mx-auto">
                        {o.leads.map((l) => (
                            <div
                                key={l.name}
                                className="flex flex-col items-center"
                            >
                                <img
                                    src={l.photo}
                                    alt={l.name}
                                    className="w-40 h-40 rounded-full object-cover shadow-lg mb-5"
                                />
                                <h3 className="text-xl font-bold text-[var(--acd-navy-900)]">
                                    {l.name}
                                </h3>
                                <p className="text-sm text-[color:var(--acd-muted)] mt-1 max-w-xs">
                                    {l.role}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="py-16 md:py-20 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="acd-eyebrow">Serviços disponíveis</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-[var(--acd-navy-900)] mt-3">
                            Áreas de atuação em {o.city}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {AREAS_HOME.map((a) => (
                            <Link
                                to={`/areas-de-atuacao#${a.anchor}`}
                                key={a.title}
                                className="acd-card p-6 block"
                            >
                                <h3 className="font-bold text-[var(--acd-navy-900)] mb-2">
                                    {a.title}
                                </h3>
                                <p className="text-sm text-[color:var(--acd-muted)] line-clamp-3">
                                    {a.text}
                                </p>
                                <span className="inline-flex items-center gap-2 text-sm font-bold text-[var(--acd-blue-600)] mt-3">
                                    Saber mais <ArrowRight size={14} />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 px-6 bg-[var(--acd-navy-900)] text-white text-center">
                <div className="max-w-3xl mx-auto">
                    <span className="acd-eyebrow">Fale connosco</span>
                    <h2 className="text-2xl md:text-4xl font-bold text-white mt-3 mb-4">
                        Precisa de um advogado em {o.city}?
                    </h2>
                    <p className="text-white/80 mb-8">
                        Agende uma reunião com a nossa equipa. Analisamos o seu
                        caso com transparência e proximidade.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a
                            href={SITE.whatsappUrlAppointment}
                            target="_blank"
                            rel="noreferrer"
                            className="acd-btn-gradient"
                        >
                            Agendar atendimento
                        </a>
                        <Link
                            to="/#contactos"
                            className="acd-btn-outline !text-white !border-white/50 hover:!bg-white/10"
                        >
                            Formulário de contacto
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OfficePage;
